import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import cx from "classnames";
import { Weekly, Loading, Heading, ThemeStore, CardNotification, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { useWarnings } from "../../hooks/useWarnings";
import { AppContext } from "../../context/EditSessionsContext";
import { Week, Types as LinkTypes } from "../../context/linkData.reducer";
import { EditedSession, FormPageType, Types } from "../../context/formData.reducer";
import FormItem from "../FormEdit/FormItem";
import WeeksMultiSelect from "../../../components/WeeksMultiSelect";
import { useGetTermPartsByCategory } from "../../../hooks/useGetTermPartsByCategory";
import { TermPartsByCategory } from "@modules/sections/ISections";
import {
  isWeekLockedForAllSelectedSessions,
  isWeekOutsideEditableThresholdForAnySelectedSession,
} from "../../utils/isWeekLocked";
import { useLoadBlockingThreshold } from "../../hooks/useLoadBlockingThreshold";
import { useWeekLockContext } from "../../hooks/useWeekLockContext";
import { ContextEDH } from "@context/ContextEDH";
import WeekTooltipLabel from "@components/week-tooltip-label";
import ReadableValuesList from "../readable-values-list";
import css from "./repeat.module.scss";

export interface IWeeklyItem {
  id: string;
  label: string;
  disabled: boolean;
  state?: "active" | "current";
  tooltip: { label: string };
  onClick: (item: IWeeklyItem) => void;
  highlight?: boolean;
}

interface IRepeatProps {
  originalWeeksBySessionId: { [key: string]: string[] };
  disabled?: boolean;
}

const getUpdatedIntervals = (intervals: Week[], weeks: { [id: string]: { checked: boolean } }) => {
  return intervals.map(interval => {
    const week = weeks && weeks?.[interval?.id] ? weeks[interval.id] : null;

    if (week) {
      return {
        ...interval,
        checked: week?.checked,
      };
    }

    return interval;
  });
};

const Repeat: React.FC<IRepeatProps> = ({ originalWeeksBySessionId, disabled = false }) => {
  const { state: stateEDH } = useContext(ContextEDH);
  const { state, dispatch } = useContext(AppContext);
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const { t } = useTranslation();

  const [isReadOnly, setIsReadOnly] = useState(!state?.form?.isEditionEnabled?.["intervals"]);
  const [warnings, setWarnings] = useWarnings(
    {
      numberOfSelectedWeeks: {
        message:
          "assignation-edit.form.notifications.weeks-repeat-validations.number-of-selected-weeks",
        active: false,
        predicate: (form: FormPageType) => {
          return !(form?.editedSessions?.intervals ?? []).some(interval => interval.checked);
        },
      },
    },
    form => !form?.savedSessionsToCreateIds && !form?.selectedSessions?.length,
  );
  const weeks = state?.form?.editedSessions?.intervals;
  const errors = state?.form?.errors;
  const thresholdDate = state?.form?.blockingThreshold;

  const weekLockContext = useWeekLockContext(originalWeeksBySessionId, disabled);

  const { loadBlockingThreshold, loading: isLoadingThreshold } = useLoadBlockingThreshold();

  const [termPartsByCategory, getTermPartsByCategory] = useGetTermPartsByCategory({
    onMount: () => (termPartsByCategory: TermPartsByCategory[]) => {
      if (!state?.link?.termPartsByCategory?.length && termPartsByCategory?.length) {
        dispatch({ type: LinkTypes.TermPartsByCategory, payload: termPartsByCategory });
      }
    },
    linkId: state?.link?.info?.id,
  });

  useEffect(() => {
    getTermPartsByCategory();
    loadBlockingThreshold();
  }, []);

  /**
   * Handle Week's form validations
   */
  useEffect(() => {
    setWarnings(state?.form);

    const selectedSessions = (state?.form?.selectedSessions ?? []).filter(
      (session: EditedSession) => !session?.id?.includes("-"),
    );

    const currentIntervalIds = (state?.form?.editedSessions?.intervals ?? [])
      .filter(interval => interval.checked)
      .map(interval => interval.id);

    const newRemovedWeekIdsBySessionId: { [key: string]: string[] } = {};

    for (const session of selectedSessions) {
      const originalWeekIds = originalWeeksBySessionId[session.id] ?? [];
      newRemovedWeekIdsBySessionId[session.id] = originalWeekIds.filter(
        id => !currentIntervalIds.includes(id),
      );
    }

    dispatch({ type: Types.SetRemovedWeekIdsBySessionId, payload: newRemovedWeekIdsBySessionId });
  }, [state?.form?.editedSessions?.intervals]);

  const handleWeekCheckboxClick = useCallback(
    (clickedWeek: Week) => () => {
      const intervals = getUpdatedIntervals(weeks, {
        [clickedWeek.id]: { checked: !clickedWeek.checked },
      });

      const weeksToModify = {
        [clickedWeek.id]: {
          ...clickedWeek,
          checked: !clickedWeek.checked,
        },
      };

      dispatch({
        type: Types.IntervalsEditedSessions,
        payload: { intervals, weeks: weeksToModify, originalWeeksBySessionId },
      });
    },
    [weeks, thresholdDate],
  );

  const { weeksByYear, lastBlockedWeek } = useMemo(() => {
    const byYear: { [year: string]: Week[] } = (weeks ?? []).reduce<{ [year: string]: Week[] }>(
      (acc, week) => {
        const year = dayjs(week.startingDate).year();
        (acc[year] = acc[year] || []).push(week);
        return acc;
      },
      {},
    );

    let lastBlocked: { week: Week; year: number; label: string } | null = null;
    let shouldShowLastBlocked = false;

    const yearGroups = Object.entries(byYear).map(([year, itemWeeks]) => {
      const weeklyItems = itemWeeks.map((week, idx) => {
        const isOutsideThresholdDate = isWeekOutsideEditableThresholdForAnySelectedSession(
          week,
          weekLockContext,
        );

        if (isOutsideThresholdDate) {
          lastBlocked = { week, year: Number(year), label: String(idx + 1) };

          if (week?.checked && !shouldShowLastBlocked) {
            shouldShowLastBlocked = true;
          }
        }

        const isWeekDisabled = isWeekLockedForAllSelectedSessions(week, weekLockContext);

        return {
          id: week.id,
          label: String(idx + 1),
          isDisabled: isWeekDisabled,
          isSelected: week.checked,
          isOutsideThresholdDate,
          tooltipLabel: (
            <WeekTooltipLabel
              weekLabel={`S${week.value}`}
              startingDate={dayjs(week.startingDate).format("DD.MM")}
              endingDate={dayjs(week.endingDate).format("DD.MM")}
              isNotEditable={isOutsideThresholdDate}
            />
          ) as any,
          onClick: !isWeekDisabled ? handleWeekCheckboxClick(week) : () => null,
          className: week.highlight
            ? cx(
                css.weeklyItem__highlight,
                isOutsideThresholdDate && css.weeklyItem__outsideThreshold,
              )
            : "",
        };
      });

      return { year, weeklyItems };
    });

    return {
      weeksByYear: yearGroups,
      lastBlockedWeek: shouldShowLastBlocked ? lastBlocked : null,
    };
  }, [weeks, weekLockContext, handleWeekCheckboxClick]);

  /**
   * Transform the `highlight` into `checked` in the context's intervals.
   */
  const applySelectionOnContext = (weeksToModify: { [key: number]: Week }) => {
    if (!weeks?.length) return;

    dispatch({
      type: Types.IntervalsEditedSessions,
      payload: {
        intervals: getUpdatedIntervals(weeks, weeksToModify as any),
        weeks: weeksToModify,
        originalWeeksBySessionId,
      },
    });
  };

  /**
   * Highlight all `intervals` whose `id` is in the `weekIdsToHighlight`
   * object.
   */
  const highlightSelectionOnContext = (weekIdsToHighlight: { [key: number]: boolean }) => {
    if (!weeks?.length) return;

    const intervals = weeks.map(week => ({
      ...week,
      highlight:
        !!weekIdsToHighlight[week.id] ||
        isWeekOutsideEditableThresholdForAnySelectedSession(week, weekLockContext),
    }));

    dispatch({ type: Types.IntervalsEditedSessions, payload: { intervals } });
  };

  /**
   * Set all interval's highlight to the given param
   */
  const setWeeksHighlight = (highlight: boolean) => {
    if (!weeks?.length) return;
    dispatch({
      type: Types.IntervalsEditedSessions,
      payload: {
        intervals: weeks?.map(week => ({
          ...week,
          highlight: highlight && week?.isInTerm,
        })),
      },
    });
  };

  const readableSelectedWeeksByYear = useMemo(() => {
    const selectedByYear = [];

    (weeksByYear ?? [])?.forEach(year => {
      const selectedWeeks = [...(year?.weeklyItems ?? [])].filter(week => week.isSelected);

      const item = {
        title: year.year,
        value: `| Semana: ${selectedWeeks?.map(week => week?.label ?? "")?.join(", ")}`,
      };

      if (selectedWeeks?.length) {
        selectedByYear.push(item);
      }
    });

    return selectedByYear;
  }, [weeksByYear]);

  return (
    <FormItem
      title={t("assignation-edit.form.repeat-section.title")}
      type="intervals"
      isDisabled={disabled && !stateEDH?.base?.base?.user?.abilities?.can_unassign_intervals}
      // Editing weeks for several sessions at once starts from a blank
      // calendar whenever they do not share the same weeks, and applying it
      // would unassign whatever each session had. Without the ability to edit
      // past weeks that is not a legal outcome, so the edition is blocked and
      // the user is pointed at editing one session at a time.
      blockedWarning={
        weekLockContext.canEditPastSessions
          ? undefined
          : {
              title: t("assignation-edit.form.content-wrapper.shared-weeks-blocked-title"),
              description: t("assignation-edit.form.content-wrapper.shared-weeks-blocked-warning"),
            }
      }
      onShowReadOnlyContent={setIsReadOnly}
    >
      {isLoadingThreshold && <Loading />}

      {isReadOnly ? (
        <ReadableValuesList className={css.readOnlyValues} items={readableSelectedWeeksByYear} />
      ) : (
        <motion.div
          initial={{ opacity: 0, height: "32px" }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.25 }}
        >
          <section className={css.repeat}>
            {!!lastBlockedWeek && (
              <CardNotification
                className={css.repeat_blockedWeekNotification}
                title={t("assignation-edit.past-sessions.blocked-weeks-notification.title")}
                state="info"
              >
                <Text type="sm">
                  {t("assignation-edit.past-sessions.blocked-weeks-notification.body-prefix")}
                  <b>S{lastBlockedWeek?.label}</b>
                  {t("assignation-edit.past-sessions.blocked-weeks-notification.body-suffix", {
                    year: lastBlockedWeek?.year,
                  })}
                </Text>
              </CardNotification>
            )}

            <WeeksMultiSelect
              disabled={weekLockContext.hasDeletedSessionSelected || disabled}
              applySelectionOnContext={applySelectionOnContext}
              highlightSelectionOnContext={highlightSelectionOnContext}
              onAllWeeksSelected={() => setWeeksHighlight(true)}
              weeks={weeks}
              termPartsByCategory={termPartsByCategory}
            />

            <section className={css.repeat_content}>
              <div
                className={cx(
                  css.weeklyContainer,
                  weeksByYear?.length <= 1 && css.weeklyContainer__singleWeekly,
                )}
              >
                {weeksByYear?.map(({ year, weeklyItems }) => (
                  <div key={year} className={css.weeklyContainer_weekly}>
                    <Heading className={css.weeklyLabel} type="h3">
                      {year}
                    </Heading>
                    <Weekly
                      className={cx(css.weeklyWeeks, themeMode === "dark" && css.weeklyWeeks__dark)}
                      weeklyItems={weeklyItems}
                    />
                  </div>
                ))}
              </div>

              {errors?.map((error, index) =>
                error.type === "Intervals" ? (
                  <CardNotification
                    key={index}
                    className={css.weeklyWarning}
                    state="error"
                    title={t("assignation-edit.form.notifications.weeks-repeat-validations.title")}
                    outlined
                  >
                    {error?.message}
                  </CardNotification>
                ) : null,
              )}
            </section>
          </section>
        </motion.div>
      )}

      {warnings.map((warning, index) => (
        <CardNotification
          key={index}
          className={css.weeklyWarning}
          state="warning"
          title={t("assignation-edit.form.notifications.weeks-repeat-validations.title")}
          outlined
        >
          {t(warning)}
        </CardNotification>
      ))}
    </FormItem>
  );
};

export default Repeat;
